import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { PageHeader } from "@/components/shared/PageHeader";
import { TableCard } from "@/components/shared/TableCard";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TablePagination } from "@/components/shared/TablePagination";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  listPlatformQuoteRequests,
  type PlatformQuoteRequestListItem,
} from "@/services/platformQuoteRequestsService";
import {
  ADMIN_STATUS_FILTER_OPTIONS,
  ADMIN_STATUS_VARIANTS,
  adminStatusLabelKey,
  type AdminQuoteRequestStatus,
} from "@/utils/adminQuoteRequestView";

/**
 * Admin queue for quote_request rows. Defaults to the AI-triage-failed queue
 * (spec §7/§10: POST /platform/quote-requests/:id/route), but the status
 * filter lets an admin browse the full lifecycle — the backend has always
 * supported ?status=<any value>, this page just didn't expose it. Real
 * backend — no mock data here. Clicking a row navigates to the detail page,
 * same convention as /requests -> /requests/:id.
 */
export function QuoteRequestsTriagePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Defaults to "all", not "triage_failed". This page is named for triage and
  // was built as a routing queue, but triage_failed is a near-empty set — so
  // the default made a COMPLETE admin surface read as missing entirely.
  // The triage_failed filter is still one click away in the dropdown.
  const [statusFilter, setStatusFilter] = useState<AdminQuoteRequestStatus | "all">("all");
  const pagination = usePaginationFromUrl();

  // Changing the filter changes which rows exist, so page 3 of the old filter
  // is meaningless under the new one -- go back to the first page rather than
  // land the admin on an empty table.
  const { goToPage } = pagination;
  const onStatusFilterChange = useCallback(
    (value: AdminQuoteRequestStatus | "all") => {
      setStatusFilter(value);
      goToPage(1);
    },
    [goToPage],
  );

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["platform", "quote-requests", statusFilter, pagination.page, pagination.limit],
    queryFn: () =>
      listPlatformQuoteRequests({
        status: statusFilter === "all" ? undefined : statusFilter,
        limit: pagination.limit,
        offset: (pagination.page - 1) * pagination.limit,
      }),
  });

  const { setTotal } = pagination;
  useEffect(() => {
    if (data != null) setTotal(data.total);
    else if (isError) setTotal(0);
  }, [data, isError, setTotal]);

  // Rows leave this queue as admins resolve them, so the last page can vanish
  // under someone standing on it. Without this they get an empty table and no
  // pagination control to climb back with — TablePagination hides itself when
  // the page it would render has nothing on it.
  const { page, totalPages, total: totalRows } = pagination;
  useEffect(() => {
    if (totalRows > 0 && page > totalPages) goToPage(totalPages);
  }, [totalRows, page, totalPages, goToPage]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={t("platform.quoteRequestsTriage.title")}
        description={t("platform.quoteRequestsTriage.description")}
      />

      <div className="mt-4 flex items-center justify-end">
        <Select value={statusFilter} onValueChange={(value) => onStatusFilterChange(value as AdminQuoteRequestStatus | "all")}>
          <SelectTrigger className="w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ADMIN_STATUS_FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <TableCard className="mt-4">
        {isLoading ? (
          <LoadingLogo />
        ) : items.length === 0 ? (
          <EmptyState
            title={t("platform.quoteRequestsTriage.emptyTitle")}
            message={t("platform.quoteRequestsTriage.emptyMessage")}
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("platform.quoteRequestsTriage.colRequest")}</TableHead>
                  <TableHead>{t("platform.quoteRequestsTriage.colRequester")}</TableHead>
                  <TableHead>{t("platform.quoteRequestsTriage.colStatus")}</TableHead>
                  <TableHead>{t("platform.quoteRequestsTriage.colDiscipline")}</TableHead>
                  <TableHead className="text-right">
                    {t("platform.quoteRequestsTriage.colConfidence")}
                  </TableHead>
                  <TableHead>{t("platform.quoteRequestsTriage.colDate")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/platform/quote-requests/${item.id}`)}
                  >
                    <TableCell className="max-w-md">
                      <p className="font-medium text-foreground">{item.title}</p>
                      {/*
                        line-clamp-1, NOT truncate. `truncate` needs a width
                        constraint to do anything, and a table cell is
                        auto-width — it would silently stop truncating on a
                        wide monitor, which is the original bug returning for
                        exactly the people whose screens hide it (spec
                        2d40b7e, control C-4). The full text is on the detail.
                      */}
                      <p className="line-clamp-1 text-sm text-muted-foreground">
                        {item.description}
                      </p>
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.requesterName ?? "—"}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        variant={ADMIN_STATUS_VARIANTS[item.status as AdminQuoteRequestStatus] ?? "info"}
                      >
                        {t(adminStatusLabelKey(item.status))}
                      </StatusBadge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {/* Read off the LEGS: `discipline` is "undecided" on a
                          two-discipline request, so rendering it would show
                          nothing for exactly the rows needing most attention. */}
                      {legLabel(item) || "—"}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {/* Absent, not 0% — a request the triage never scored
                          has no confidence, and "0%" would read as certainty
                          that it is wrong. */}
                      {item.triageResult
                        ? `${Math.round(item.triageResult.confidence * 100)}%`
                        : "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {formatDisplayDate(item.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("platform.quoteRequestsTriage.rowsPerPage")}
              pageOfLabel={t("platform.quoteRequestsTriage.pageOf", {
                from: total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(pagination.page * pagination.limit, total),
                total,
              })}
              onRefresh={() => void refetch()}
              isRefreshing={isFetching}
            />
          </div>
        )}
      </TableCard>
    </div>
  );
}

/**
 * What disciplines this request is actually running, from its legs.
 *
 * Falls back to the parent's `discipline` for rows that predate legs; returns
 * null when there is nothing meaningful to show, so the caller renders no line
 * rather than the word "undecided".
 */
function legLabel(item: PlatformQuoteRequestListItem): string | null {
  const labelFor = (d: string): string =>
    d === "notary" ? "Escribano" : d === "accountant" ? "Contador" : d;
  const legs = item.legDisciplines ?? [];
  if (legs.length > 0) return legs.map(labelFor).join(" + ");
  if (item.discipline && item.discipline !== "undecided") return labelFor(item.discipline);
  return null;
}
